import { startOfDay, isAfter, parseISO } from 'date-fns'
import type React from 'react'
import { useState, useContext } from 'react'
import styled from 'styled-components'

import { Button, AlternateButton } from '@/components/DEPRECATED_UI/Button'
import SlideInSidebar from '@/components/DEPRECATED_UI/SlideInSidebar'
import type { SelectOption } from '@/components/Select'
import TextInput from '@/components/TextInput'
import ctx from '@/context/AppContext'
import { graphQLQuery } from '@/hooks/hooks'
import QUERIES from '@/lib/graphql'
import type { Income } from '@/types/Activity'
import { utcDateTimeStringToDateString } from '@/utils/date'
import { isAutomaticallyVerifiedIncome, isEmploymentIncome } from '@/utils/income'
import { Colours } from '@/utils/styles'

import PreviewStep from './PreviewStep'
import type { OverrideIncomeRequest } from './types'

interface SubjectData {
  customerId: string
  applicationId: string
  subjectId: string
}

interface Props {
  income: Income
  setOpen: (open: boolean) => void
  subjectData: SubjectData
  onSuccess: () => void
}

const ItemHeader = styled.h2`
  font-family: Montserrat;
  font-size: 14px;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  -webkit-letter-spacing: 0.21px;
  -moz-letter-spacing: 0.21px;
  -ms-letter-spacing: 0.21px;
  letter-spacing: 0.21px;
  text-align: left;

  margin-top: 20px;
  margin-bottom: 0;

  #document-proof {
    font-weight: normal;
  }
`

const Container = styled.form`
  display: grid;
  grid-template-rows: 100px 1fr min-content;
  height: 100%;
  grid-gap: 15px;
`

const InputGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  margin-top: 10px;
  grid-gap: 10px;

  span:not(#text-input) {
    display: none;
  }
`

const Select = styled.select`
  background: white;
  border-style: solid;
  border-color: rgb(118, 118, 118);
  border-width: 1px;
  padding: 10px;
  border-radius: 3px;
  position: relative;
  text-align: left;
  outline: none;
`

const VERIFICATION_OPTIONS = [
  { label: 'Payslip', value: 'payslip' },
  { label: 'Bank Statement', value: 'bankStatement' },
  { label: 'Self Assessment Tax Form', value: 'selfAssessmentTaxForm' },
  { label: 'Pension Document', value: 'pensionDocument' },
  { label: 'Investment Document', value: 'investmentDocument' },
  { label: 'Benefit Document', value: 'benefitDocument' },
  {
    label: 'Signed Contract Of Employment',
    value: 'signedContractOfEmployment',
  },
  { label: 'Award Document', value: 'awardDocument' },
  { label: 'Open Banking', value: 'openBanking' },
  { label: 'Student Scholarship', value: 'studentScholarship' },
  { label: 'Other', value: 'other' },
]

const EMPLOYMENT_VERIFICATION_OPTIONS = [
  { label: 'Konfir - payroll provider', value: 'payrollProvider' },
  { label: 'Konfir - HMRC', value: 'HMRC' },
]

const INCOME_REFEREE_VERIFICATION_OPTIONS = [
  { label: 'Referee Response', value: 'refereeResponse' },
]

type Frequency = 'annually' | 'quarterly' | 'monthly' | 'weekly' | 'daily' | 'hourly'

const FREQUENCY_OPTIONS: SelectOption<Frequency>[] = [
  { label: 'Annually', value: 'annually' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Daily', value: 'daily' },
  { label: 'Hourly', value: 'hourly' },
]

const BONUS_FREQUENCY_OPTIONS: SelectOption<Frequency>[] = [
  { label: 'Annually', value: 'annually' },
  { label: 'Quarterly', value: 'quarterly' },
]

type ContractType = 'permanent' | 'temporary' | 'fixed term'

const CONTRACT_TYPE_OPTIONS: SelectOption<ContractType>[] = [
  { label: 'Permanent', value: 'permanent' },
  { label: 'Temporary', value: 'temporary' },
  { label: 'Fixed Term', value: 'fixed term' },
]

const toContractTypeOption = (contractType?: string): ContractType => {
  switch (contractType) {
    case 'Temporary':
      return 'temporary'
    case 'Fixed-term':
      return 'fixed term'
  }

  return 'permanent'
}

const toContractTypePayload = (contractType: ContractType): string => {
  switch (contractType) {
    case 'permanent':
      return 'Permanent'
    case 'fixed term':
      return 'Fixed-term'
    case 'temporary':
      return 'Temporary'
  }
}

const hasContractEndDate = (contractType: ContractType): boolean =>
  contractType === 'fixed term' || contractType === 'temporary'

const genericErrorMessage =
  'Failed to verify income. Please retry in a few minutes and contact support if issues persist.'

const ErrorContainer = styled.div`
  color: ${Colours.red};
`

type FormStep = 'form' | 'preview' | 'submit'

const toFrequencyPayload = (frequency: Frequency) => {
  switch (frequency) {
    case 'annually':
      return 'Annually'
    case 'quarterly':
      return 'Quarterly'
    case 'monthly':
      return 'Monthly'
    case 'weekly':
      return 'Weekly'
    case 'daily':
      return 'Daily'
    case 'hourly':
      return 'Hourly'
  }
}

const VerifyIncomePanel: React.FC<Props> = ({
  income,
  setOpen,
  subjectData,
  onSuccess,
}) => {
  const [amount, setAmount] = useState('')
  const [verifiedBy, setVerifiedBy] = useState(VERIFICATION_OPTIONS[0].value)
  const [hoursPerWeek, setHoursPerWeek] = useState(
    income?.incomeProperties.averageHoursAWeek ?? '',
  )
  const [frequency, setFrequency] = useState<Frequency>('annually')
  const [bonusAmount, setBonusAmount] = useState('')
  const [bonusFrequency, setBonusFrequency] = useState<Frequency>('annually')
  const [selectedContractType, setSelectedContractType] = useState<ContractType>(
    toContractTypeOption(income?.contractType),
  )

  const hasIncomeReference =
    income?.employerReference || income?.accountantReference || null
  const today = new Date()

  const defaultStartDate = income?.startDate ?? today.toISOString()
  const defaultEndDate = income?.endDate ?? today.toISOString()

  const [contractStartDate, setContractStartDate] = useState<string>(
    utcDateTimeStringToDateString(defaultStartDate),
  )
  const [contractEndDate, setContractEndDate] = useState<string>(
    utcDateTimeStringToDateString(defaultEndDate),
  )

  const [validationError, setValidationError] = useState<string | undefined>(undefined)
  const [error, setError] = useState<string | undefined>(undefined)

  const [sending, setSending] = useState(false)

  const [formStep, setFormStep] = useState<FormStep>('form')

  const { personId } = useContext(ctx)

  const automaticallyVerifiedIncome = isAutomaticallyVerifiedIncome(income)

  const employmentOptions = hasIncomeReference
    ? [...INCOME_REFEREE_VERIFICATION_OPTIONS, ...EMPLOYMENT_VERIFICATION_OPTIONS]
    : [...EMPLOYMENT_VERIFICATION_OPTIONS]

  const incomeOptions = [...VERIFICATION_OPTIONS]

  const incomeType = income?.type

  const incomeVerificationOptions = isEmploymentIncome(incomeType)
    ? [...incomeOptions, ...employmentOptions]
    : [...incomeOptions]

  const overrideIncomeRequest: OverrideIncomeRequest = {
    subjectData,
    incomeId: income?.id,
    validatorId: personId,
    ...(automaticallyVerifiedIncome && { automaticallyVerified: true }),
    ...(!automaticallyVerifiedIncome && {
      validationSources: [verifiedBy],
      incomeOverride: {
        value: +amount,
        averageHoursAWeek: hoursPerWeek ? +hoursPerWeek : undefined,
        frequency: toFrequencyPayload(frequency),
      },
    }),
    ...(income?.type === 'employment' && {
      guaranteedBonusOverride: {
        value: +bonusAmount,
        frequency: toFrequencyPayload(bonusFrequency),
      },
      incomeContractTermRequest: {
        contractType: toContractTypePayload(selectedContractType),
        startDate: contractStartDate,
        ...(hasContractEndDate(selectedContractType) && {
          endDate: contractEndDate,
        }),
      },
    }),
  }

  const handleProceed = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault()
    }

    setValidationError(undefined)

    if (
      income?.type === 'employment' &&
      hasContractEndDate(selectedContractType) &&
      !isAfter(
        startOfDay(parseISO(contractEndDate)),
        startOfDay(parseISO(contractStartDate)),
      )
    ) {
      setValidationError('Contract end date must be after the contract start date.')
      return
    }

    if (amount !== '' && amount.includes(',')) {
      setValidationError('Income value must not contain a comma')
      return
    }

    if (bonusAmount !== '' && bonusAmount.includes(',')) {
      setValidationError('Guaranteed bonus value must not contain a comma.')
      return
    }

    setFormStep('preview')
  }

  function handleSubmit(e?: React.FormEvent) {
    setFormStep('submit')

    if (e) e.preventDefault()
    setSending(true)
    setError(undefined)

    graphQLQuery({
      query: QUERIES.overrideIncome,
      variables: {
        overrideIncomeRequest,
      },
    })
      .then(resData => resData.json())
      .then(data => {
        if (data.errors) {
          setError(genericErrorMessage)
          return
        }

        onSuccess()
      })
      .catch(err => {
        console.log(err)
        setError(genericErrorMessage)

        setSending(false)
      })
  }

  if (!income) return null

  return (
    <SlideInSidebar
      onClose={() => setOpen(false)}
      header={<h2>Verify Income</h2>}
      content={
        formStep === 'form' ? (
          <Container onSubmit={handleProceed}>
            <div>
              <p>This panel can be used in one of two ways</p>
              <ul>
                <li>Confirm you have viewed and verified the applicants income.</li>
                <li>Update and confirm the applicants income to a different value.</li>
              </ul>
            </div>
            <div>
              {!automaticallyVerifiedIncome && (
                <div>
                  <ItemHeader>Income value and frequency</ItemHeader>
                  <InputGroup>
                    <TextInput
                      onChange={value => setAmount(value)}
                      value={amount}
                      placeholder="Income Value (£)"
                    />
                    <Select
                      value={frequency}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        setFrequency(e.target.value as Frequency)
                      }
                    >
                      {FREQUENCY_OPTIONS.map(({ label, value }, i) => (
                        <option key={i} value={value}>
                          {label}
                        </option>
                      ))}
                    </Select>
                  </InputGroup>
                  {
                    // eslint-disable-next-line eqeqeq
                    frequency == 'hourly' && (
                      <>
                        <TextInput
                          onChange={value => setHoursPerWeek(value)}
                          value={hoursPerWeek}
                          label="Average Hours Per Week"
                        />
                      </>
                    )
                  }
                  {
                    // eslint-disable-next-line eqeqeq
                    income.type == 'employment' && (
                      <>
                        <ItemHeader>Guaranteed bonus value and frequency</ItemHeader>
                        <InputGroup>
                          <TextInput
                            onChange={value => setBonusAmount(value)}
                            value={bonusAmount}
                            placeholder="Bonus Value (£)"
                          />
                          <Select
                            value={bonusFrequency}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                              setBonusFrequency(e.target.value as Frequency)
                            }
                          >
                            {BONUS_FREQUENCY_OPTIONS.map(({ label, value }, i) => (
                              <option key={i} value={value}>
                                {label}
                              </option>
                            ))}
                          </Select>
                        </InputGroup>
                        <ItemHeader>Contract type</ItemHeader>
                        <InputGroup>
                          <Select
                            value={selectedContractType}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                              console.log(e.target.value)
                              setSelectedContractType(e.target.value as ContractType)
                            }}
                          >
                            {CONTRACT_TYPE_OPTIONS.map(({ label, value }, i) => (
                              <option key={i} value={value}>
                                {label}
                              </option>
                            ))}
                          </Select>
                        </InputGroup>
                        <ItemHeader>Contract start date</ItemHeader>
                        <InputGroup>
                          <TextInput
                            type="date"
                            value={contractStartDate}
                            onChange={value => setContractStartDate(value)}
                          />
                        </InputGroup>
                        {hasContractEndDate(selectedContractType) && (
                          <>
                            <ItemHeader>Contract end date</ItemHeader>
                            <InputGroup>
                              <TextInput
                                type="date"
                                value={contractEndDate}
                                onChange={value => setContractEndDate(value)}
                              />
                            </InputGroup>
                          </>
                        )}
                      </>
                    )
                  }
                  <ItemHeader>
                    I have verified this income by checking the applicants
                  </ItemHeader>
                  <InputGroup>
                    <Select
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        setVerifiedBy(e.target.value)
                      }
                      value={verifiedBy}
                    >
                      {incomeVerificationOptions.map(({ label, value }, i) => (
                        <option key={i} value={value}>
                          {label}
                        </option>
                      ))}
                    </Select>
                  </InputGroup>
                  {verifiedBy === 'studentScholarship' && (
                    <p>
                      <strong>Warning:</strong> Choosing Student Scholarship as the
                      verification method for a Tenant will update their affordability
                      ratio to be 1:1 for ALL of their added incomes.
                    </p>
                  )}
                </div>
              )}
              {automaticallyVerifiedIncome && (
                <div>
                  <p>Incomes of this type cannot have their values changed</p>
                </div>
              )}
            </div>

            <div>
              {validationError && <ErrorContainer>{validationError}</ErrorContainer>}
              <div>
                <AlternateButton onClick={() => setOpen(false)}>Cancel</AlternateButton>
                <Button onClick={handleProceed}>Proceed</Button>
              </div>
            </div>
          </Container>
        ) : formStep === 'preview' ? (
          <PreviewStep
            overrideIncomeRequest={overrideIncomeRequest}
            onConfirm={handleSubmit}
            onBack={() => setFormStep('form')}
          />
        ) : (
          <>
            {sending && <p>Loading...</p>}
            <div>{error}</div>
            <AlternateButton onClick={() => setFormStep('preview')}>
              Cancel
            </AlternateButton>
          </>
        )
      }
    />
  )
}

export default VerifyIncomePanel
